import { useDisclosure } from "@mantine/hooks";
import { useCallback, useState } from "react";

import { useChangePassword } from "../../../api/hooks/user/useChangePassword.ts";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal.tsx";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { ChangePasswordForm } from "./ChangePasswordForm.tsx";
import { SetPasswordForm } from "./SetPasswordForm.tsx";

export const PasswordForm = () => {
  const { user, refresh } = useCurrentUserContext();

  const [
    confirmationModalOpened,
    { open: openConfirmationModal, close: closeConfirmationModal },
  ] = useDisclosure(false);
  const [formValue, setFormValue] = useState<{
    password1: string;
    password2: string;
  }>();

  const [{ fetching }, changePassword] = useMutationNotificationWrapper(
    useChangePassword(),
    {
      success: {
        message: "Password has been changed successfully",
      },
    }
  );

  const handleOpenConfirmationModal = useCallback(
    (value: { password1: string; password2: string }) => {
      setFormValue(value);
      openConfirmationModal();
    },
    [openConfirmationModal]
  );

  const handleCloseConfirmationModal = useCallback(() => {
    setFormValue(undefined);
    closeConfirmationModal();
  }, [closeConfirmationModal]);

  const handleSubmit = useCallback(async () => {
    if (!formValue) {
      return;
    }
    closeConfirmationModal();
    changePassword({ input: formValue }).then(refresh);
  }, [changePassword, closeConfirmationModal, formValue, refresh]);

  return (
    <>
      {user?.hasPassword ? (
        <ChangePasswordForm
          submitting={fetching}
          onSubmit={handleOpenConfirmationModal}
        />
      ) : (
        <SetPasswordForm
          submitting={fetching}
          onSubmit={handleOpenConfirmationModal}
        />
      )}
      {confirmationModalOpened && (
        <ConfirmationModal
          opened={true}
          message={"Are you sure you want to update your password?"}
          onConfirm={handleSubmit}
          onClose={handleCloseConfirmationModal}
        />
      )}
    </>
  );
};
